export const DOCUMENT_TYPES = [
  'welcome_letter',
  'plan_certificate',
  'cancellation_confirmation',
  'renewal_notice',
  'general',
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export const ASSIGNABLE_DOCUMENT_TYPES = [
  'welcome_letter',
  'plan_certificate',
  'cancellation_confirmation',
  'renewal_notice',
] as const satisfies readonly DocumentType[];

export type AssignableDocumentType = (typeof ASSIGNABLE_DOCUMENT_TYPES)[number];

export type TemplateVersionStatus = 'draft' | 'published' | 'superseded';

export type TemplateStatusFilter = 'published' | 'draft_only';

export type TemplateOwner = 'platform' | 'dealer';

export interface TemplateVersionSummary {
  id: string;
  templateId: string;
  versionNumber: number;
  status: TemplateVersionStatus;
  revision: number;
  changeNote: string | null;
  publishedByName: string | null;
  publishedAt: string | null;
  createdByName: string | null;
  updatedByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TemplateVersion extends TemplateVersionSummary {
  bodyHtml: string;
}

export interface DocumentTemplateSummary {
  id: string;
  key: string;
  name: string;
  description: string | null;
  documentType: DocumentType;
  owner: TemplateOwner;
  ownerDealerId: string | null;
  published: TemplateVersionSummary | null;
  hasDraft: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TemplateSource {
  templateId: string;
  templateName: string | null;
  versionId: string | null;
  versionNumber: number | null;
  newerVersionAvailable: boolean;
}

export interface DocumentTemplateDetail extends DocumentTemplateSummary {
  draft: TemplateVersion | null;
  source: TemplateSource | null;
}

export interface DocumentTemplateListParams {
  search?: string;
  documentType?: DocumentType;
  status?: TemplateStatusFilter;
  owner?: TemplateOwner;
  dealerId?: string;
  availableToDealerId?: string;
  sort?: 'name' | 'updated_at';
  direction?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export interface PaginationMeta {
  currentPage: number;
  lastPage: number;
  perPage: number;
  total: number;
  from: number;
  to: number;
}

export interface DocumentTemplateListResult {
  templates: DocumentTemplateSummary[];
  meta: PaginationMeta;
}

export interface TemplateMetadataInput {
  name: string;
  description: string;
  documentType: DocumentType;
}

export interface CreateTemplateInput extends TemplateMetadataInput {
  dealerId: string | null;
  key: string;
}

export interface CustomiseTemplateInput {
  dealerId: string;
  key: string;
  name: string;
}

export interface SaveDraftInput {
  bodyHtml: string;
  changeNote: string;
  revision: number;
}

export interface DocumentVariable {
  key: string;
  label: string;
  type: string;
  description: string;
  sample: string;
  namespace: string;
}

export interface VersionHistoryResult {
  versions: TemplateVersionSummary[];
  meta: PaginationMeta;
}

export type PreviewSource = { bodyHtml: string } | { versionId: string };

export interface TemplatePreview {
  html: string;
  usedVariables: string[];
  emptyVariables: string[];
}
